/**
 * Age gate for the marketing home page.
 *
 * Opens a native <dialog> so the browser supplies the focus trap and makes the
 * page behind it inert. Escape and click-outside are both suppressed: the point
 * is that the visitor answers.
 */
class YcAgeGate extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    this.key = this.dataset.storageKey || 'yc-age-confirmed';
    this.days = Number(this.dataset.rememberDays) || 30;

    if (this.#alreadyConfirmed()) return;

    this.hidden = false;
    this.dialog.showModal();

    // Escape would otherwise dismiss it without an answer.
    this.dialog.addEventListener('cancel', (event) => event.preventDefault());

    // A native dialog closes on a backdrop click in some browsers; the click
    // lands on the dialog itself rather than the panel inside it.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) event.stopPropagation();
    });

    this.querySelector('[data-confirm]')?.addEventListener('click', () => this.#confirm());
    this.querySelector('[data-decline]')?.addEventListener('click', () => this.#decline());
  }

  #alreadyConfirmed() {
    try {
      const raw = window.localStorage.getItem(this.key);
      if (!raw) return false;
      const until = Number(raw);
      if (!Number.isFinite(until)) return false;
      if (Date.now() > until) {
        window.localStorage.removeItem(this.key);
        return false;
      }
      return true;
    } catch {
      // Private windows and blocked site data throw on access. Asking again is
      // the safe failure, so treat it as unconfirmed.
      return false;
    }
  }

  #confirm() {
    try {
      const until = Date.now() + this.days * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.key, String(until));
    } catch {
      // Not remembering is survivable; blocking the page is not.
    }
    this.dialog.close();
    this.hidden = true;
  }

  #decline() {
    const message = this.querySelector('[data-declined]');
    if (message) {
      message.hidden = false;
      message.focus?.();
    }
    this.querySelector('[data-decline]')?.setAttribute('disabled', '');
  }
}

if (!customElements.get('yc-age-gate')) customElements.define('yc-age-gate', YcAgeGate);
